import { useEffect, useMemo, useRef, useState } from "react";
import type ReactEChartsCore from "echarts-for-react";
import ReactECharts from "echarts-for-react";
import {
  addInsight,
  createDashboard,
  fetchDashboards,
  type DashboardSummary,
} from "../../api/dashboards";
import { useReport } from "../report/ReportContext";
import { DEFAULT_PALETTE } from "./palette";
import { ECHARTS_TEMPLATES, TABLE_TEMPLATE_NAME } from "./templates";
import { TableRenderer } from "./templates/Table";
import type { ChartSpec } from "./types";

interface Props {
  spec: ChartSpec;
  palette?: ReadonlyArray<string>;
  // Optional context for the Add-to-dashboard / Share affordances on
  // this card. When all three are present the card shows interactive
  // affordances. When omitted (e.g. on the public share viewer) the
  // affordances stay hidden.
  bearer?: string;
  organization?: string;
  userQuery?: string;
}

type View = "chart" | "table";

export function ChartCard({
  spec,
  palette = DEFAULT_PALETTE,
  bearer,
  organization,
  userQuery,
}: Props) {
  const canInteract = Boolean(bearer && organization);
  // Datasource the model queried for this chart — used as a small
  // provenance chip in the card header.
  const datasource = spec.datasource_id;
  // Legacy insights (saved before the v2 ChartSpec contract — query_template
  // was NULL or a different schema) arrive with no kind / template_name and
  // no rows/columns until the dashboard refresh round-trip lands. Guard
  // against undefined arrays so the whole DashboardsPage doesn't crash on
  // first paint before refresh fills them in.
  const rows = spec.rows ?? [];
  const columns = spec.columns ?? [];
  const isTableTemplate =
    !spec.kind ||
    (spec.kind === "template" && spec.template_name === TABLE_TEMPLATE_NAME);
  // Default view: table for table-template; chart for everything else.
  const [view, setView] = useState<View>(isTableTemplate ? "table" : "chart");
  const [sqlOpen, setSqlOpen] = useState(false);
  const [dashOpen, setDashOpen] = useState(false);
  const dashBtnRef = useRef<HTMLButtonElement | null>(null);
  const echartsRef = useRef<ReactEChartsCore | null>(null);
  const report = useReport();
  const alreadyQueued = report.hasChartLike(spec.sql, spec.title ?? "");
  const [justAdded, setJustAdded] = useState(false);

  // Build the chart option defensively — a malformed spec (model
  // passed a template_name but forgot the matching input fields, etc.)
  // shouldn't blank the whole answer card.
  const built = useMemo(() => {
    if (isTableTemplate) return { option: null as Record<string, unknown> | null, error: null as string | null };
    try {
      return { option: buildOption(spec, palette), error: null };
    } catch (e) {
      return { option: null, error: e instanceof Error ? e.message : String(e) };
    }
  }, [spec, palette, isTableTemplate]);

  const option = built.option;
  const buildError = built.error;

  // Detect "kind: custom but the option carries no data" — happens when
  // the model invents an ECharts option (e.g. a heatmap) and forgets to
  // inline the rows into `series[].data`. ECharts renders that as a
  // blank 380px container with no error. Treat as un-renderable so the
  // chart toggle disappears, we force the Table view, and a banner
  // explains the fallback instead of showing the empty box.
  const customWithoutData = spec.kind === "custom" && !hasAnySeriesData(option);

  // Chart only shows when we have a renderable option. Otherwise
  // (table template OR chart-build error OR empty custom) we silently
  // fall back to the table — no broken "Chart" toggle option for the
  // user to land on an empty/errored card.
  const canShowChart = !isTableTemplate && option != null && !customWithoutData;
  const effectiveView: View = canShowChart ? view : "table";
  const sql = (spec.sql ?? "").trim();

  useEffect(() => {
    if (buildError) {
      console.warn("[ChartCard] falling back to table because chart build failed:", buildError, spec);
    }
  }, [buildError, spec]);

  return (
    <div className="rounded-[20px] border border-line bg-white overflow-hidden">
      <div className="flex items-start justify-between px-5 pt-4 pb-2 gap-3">
        <div className="text-[16px] font-semibold text-neutral-900 leading-snug tracking-tight">
          {spec.title}
        </div>
        <div className="flex items-center gap-2 shrink-0 text-neutral-500">
          {canShowChart && <ViewToggle value={view} onChange={setView} />}
          {datasource && (
            <span className="hidden sm:flex items-center gap-1 text-[12px] text-neutral-500 ml-1">
              <DatabaseIcon />
              {datasource}
            </span>
          )}
          {sql && (
            <button
              type="button"
              onClick={() => setSqlOpen(true)}
              className="h-7 w-7 inline-flex items-center justify-center rounded-full text-neutral-500 hover:bg-chip hover:text-brand transition-colors"
              aria-label="View SQL"
              title="View SQL"
            >
              <SqlIcon />
            </button>
          )}
          {canInteract && (
            <div className="relative">
              <button
                ref={dashBtnRef}
                type="button"
                onClick={() => {
                  setDashOpen((v) => !v);
                }}
                className={
                  "h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-chip transition-colors " +
                  (dashOpen ? "text-brand bg-chip" : "text-neutral-500 hover:text-brand")
                }
                aria-label="Add to dashboard"
                title="Add to dashboard"
              >
                <DashboardAddIcon />
              </button>
              {dashOpen && (
                <DashboardPicker
                  bearer={bearer!}
                  organization={organization!}
                  userQuery={userQuery ?? ""}
                  chartSpec={spec}
                  anchorRef={dashBtnRef}
                  onClose={() => setDashOpen(false)}
                />
              )}
            </div>
          )}
          {canInteract && (
            <button
              type="button"
              onClick={() => {
                // Capture whatever the user is currently looking at — the
                // chart they queue is the chart they see in the PDF. If we
                // can't reach the ECharts instance (Table view, or the
                // chart never mounted), fall back gracefully by skipping
                // the add and surfacing a hint via the title.
                const inst = echartsRef.current?.getEchartsInstance?.();
                const imagePng = inst
                  ? inst.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "#ffffff" })
                  : "";
                if (!imagePng) {
                  // Nothing renderable to add; do nothing rather than
                  // queueing an empty card. Most likely the user is on
                  // the Table view — they can still add the answer text
                  // via the Add-to-Report button on the answer.
                  return;
                }
                // Top 30 rows is enough for the LLM to cite specific
                // numbers in the report without bloating the prompt.
                const sampleRows = (spec.rows ?? []).slice(0, 30);
                report.addChart({
                  title: spec.title ?? "Chart",
                  userQuery: userQuery ?? "",
                  datasourceId: spec.datasource_id,
                  imagePng,
                  rows: sampleRows,
                  columns: spec.columns ?? [],
                  chartSpec: spec,
                });
                setJustAdded(true);
                window.setTimeout(() => setJustAdded(false), 1500);
              }}
              disabled={!canShowChart || effectiveView !== "chart"}
              className={
                "h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-chip transition-colors " +
                (alreadyQueued || justAdded
                  ? "text-emerald-600"
                  : "text-neutral-500 hover:text-brand") +
                (!canShowChart || effectiveView !== "chart"
                  ? " opacity-40 cursor-not-allowed"
                  : "")
              }
              aria-label="Add to PDF report"
              title={
                effectiveView !== "chart"
                  ? "Switch to Chart view to add to report"
                  : alreadyQueued
                    ? "Already in report queue"
                    : "Add to PDF report"
              }
            >
              {alreadyQueued || justAdded ? <CheckIcon /> : <ReportPlusIcon />}
            </button>
          )}
        </div>
      </div>

      {spec.truncated && (
        <TruncationBanner
          rowCount={spec.rows.length}
          canViewSql={Boolean(sql)}
          onViewSql={() => setSqlOpen(true)}
        />
      )}

      {customWithoutData && <CustomChartUnsupportedBanner />}

      <div className="px-3 pb-3">
        {effectiveView === "table" ? (
          <TableRenderer rows={rows} columns={columns} />
        ) : (
          option && (
            <ReactECharts
              ref={echartsRef}
              option={option}
              style={{ width: "100%", height: 380 }}
              notMerge
              lazyUpdate
              opts={{ renderer: "canvas", devicePixelRatio: window.devicePixelRatio || 2 }}
            />
          )
        )}
      </div>

      {sql && sqlOpen && (
        <SqlModal sql={sql} datasource={datasource} onClose={() => setSqlOpen(false)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SQL modal — centered overlay with copy + close, mirrors v1's modal. */
/* ------------------------------------------------------------------ */

function SqlModal({ sql, datasource, onClose }: { sql: string; datasource?: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(sql);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 bg-neutral-900/20 flex items-start justify-center pt-16 px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[calc(100vh-8rem)] flex flex-col bg-white rounded-[18px] border border-line shadow-xl overflow-hidden"
      >
        <div className="shrink-0 flex items-center gap-2 px-5 pt-4 pb-3 border-b border-line">
          <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-neutral-900 bg-chip rounded-full px-2.5 py-1">
            SQL
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={copy}
              className="h-8 px-3 inline-flex items-center gap-1.5 rounded-full text-[12px] font-medium text-neutral-600 hover:bg-chip hover:text-neutral-900 transition-colors"
              title="Copy SQL"
            >
              <CopyIcon />
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="h-8 w-8 inline-flex items-center justify-center rounded-full text-neutral-500 hover:bg-chip hover:text-neutral-800 transition-colors"
              aria-label="Close"
              title="Close"
            >
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3">
          {datasource && (
            <div className="inline-flex items-center text-[12px] font-medium text-neutral-700 bg-chip rounded-full px-2.5 py-1">
              {datasource}
            </div>
          )}
          <pre className="text-[13px] leading-relaxed text-neutral-900 whitespace-pre-wrap font-mono bg-neutral-50 rounded-[12px] border border-line p-4 overflow-x-auto">
            {sql}
          </pre>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* DashboardPicker — anchored popover that lists dashboards + new.      */
/* ------------------------------------------------------------------ */

interface PickerProps {
  bearer: string;
  organization: string;
  userQuery: string;
  chartSpec: ChartSpec;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}

function DashboardPicker({ bearer, organization, userQuery, chartSpec, anchorRef, onClose }: PickerProps) {
  const [items, setItems] = useState<DashboardSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<number | "new" | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  // Two-step save flow (matches v1): once a dashboard is picked (or a
  // new one named), the user gets a chance to title the insight before
  // we POST. selectedTarget tracks which dashboard the next save
  // belongs to; insightTitle is the editable name.
  const [selectedTarget, setSelectedTarget] = useState<number | "new" | null>(null);
  const [insightTitle, setInsightTitle] = useState(chartSpec.title ?? "");
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchDashboards(bearer, organization)
      .then((d) => !cancelled && setItems(d))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [bearer, organization]);

  // Click outside / Escape to close. Ignores clicks on the anchor
  // button itself so the toggle stays well-behaved.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [anchorRef, onClose]);

  const save = async () => {
    if (selectedTarget == null) return;
    const title = insightTitle.trim() || chartSpec.title || "Untitled insight";
    setAdding(selectedTarget);
    setError(null);
    try {
      let dashboardId: number;
      if (selectedTarget === "new") {
        const dashName = newName.trim();
        if (!dashName) {
          setError("Dashboard name is required.");
          setAdding(null);
          return;
        }
        const fresh = await createDashboard(bearer, dashName, organization);
        dashboardId = fresh.id;
      } else {
        dashboardId = selectedTarget;
      }
      await addInsight(bearer, dashboardId, {
        chart_spec: chartSpec,
        user_query: userQuery,
        datasource_id: chartSpec.datasource_id ?? "",
        insight_name: title,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setAdding(null);
    }
  };

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-[320px] rounded-[14px] bg-white border border-line shadow-card z-40 overflow-hidden"
    >
      <div className="flex items-center justify-between px-3 pt-3 pb-2">
        <div className="text-[13px] text-neutral-500">Select a dashboard..</div>
        <button
          type="button"
          onClick={() => {
            setNewOpen(true);
            setSelectedTarget("new");
            setNewName("");
          }}
          className="text-[12px] font-medium text-brand hover:underline inline-flex items-center gap-1"
        >
          <span className="text-[14px] leading-none">+</span> New
        </button>
      </div>

      <div className="border-t border-line/70" />

      {/* Existing dashboards list. Tapping one selects it as the
          save target; the title-input footer appears below. */}
      <div className="max-h-[200px] overflow-y-auto py-1">
        {loading ? (
          <div className="px-3 py-2 text-[12px] text-neutral-400">Loading…</div>
        ) : items.length === 0 && !newOpen ? (
          <div className="px-3 py-3 text-[12px] text-neutral-500 leading-relaxed">
            No dashboards yet — use “+ New” above.
          </div>
        ) : (
          items.map((d) => (
            <button
              key={d.id}
              type="button"
              disabled={adding !== null}
              onClick={() => {
                setSelectedTarget(d.id);
                setNewOpen(false);
              }}
              className={
                "w-full flex items-center justify-between px-3 py-2 text-left text-[14px] transition-colors " +
                (selectedTarget === d.id
                  ? "bg-chip text-neutral-900"
                  : "text-neutral-900 hover:bg-chip/70")
              }
            >
              <span className="font-medium truncate">{d.name}</span>
              <span className="text-[11px] text-neutral-400">
                {d.insight_count} {d.insight_count === 1 ? "insight" : "insights"}
              </span>
            </button>
          ))
        )}
      </div>

      {/* Footer: revealed when a dashboard is selected (or "+ New"
          is in progress). Houses the optional new-dashboard name
          input + insight-title input + Save/Cancel. */}
      {(selectedTarget !== null || newOpen) && (
        <div className="border-t border-line/70 px-3 py-3 space-y-2">
          {newOpen && (
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter new dashboard name"
              className="w-full h-9 px-3 rounded-md border border-line text-[13px] outline-none focus:ring-1 focus:ring-brand"
            />
          )}
          <input
            value={insightTitle}
            onChange={(e) => setInsightTitle(e.target.value)}
            placeholder="Give this query a title.."
            className="w-full h-9 px-3 rounded-md border border-line text-[13px] outline-none focus:ring-1 focus:ring-brand"
            onKeyDown={(e) => {
              if (e.key === "Enter" && insightTitle.trim()) void save();
              if (e.key === "Escape") onClose();
            }}
          />
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              onClick={onClose}
              className="text-[12px] text-neutral-600 hover:text-neutral-900 px-3 h-8 rounded-md hover:bg-chip"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={
                adding !== null ||
                !insightTitle.trim() ||
                (newOpen && !newName.trim())
              }
              onClick={() => void save()}
              className="text-[12px] font-medium bg-brand text-white rounded-md px-4 h-8 hover:bg-brand-600 disabled:opacity-40"
            >
              {adding !== null ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="border-t border-line/70 px-3 py-2 text-[11px] text-red-600">{error}</div>
      )}
    </div>
  );
}

function buildOption(spec: ChartSpec, palette: ReadonlyArray<string>): Record<string, unknown> | null {
  // Legacy insights with no kind set: render-as-table; no chart option.
  if (!spec.kind) return null;
  if (spec.kind === "custom") {
    const opt = { ...spec.custom_option };
    if (!("color" in opt) || opt.color == null) opt.color = [...palette];
    return opt;
  }
  const builder = ECHARTS_TEMPLATES[spec.template_name];
  if (!builder) return null;
  return builder(spec.input, spec.rows ?? [], spec.columns ?? [], spec.title, palette);
}

/* ------------------------------------------------------------------ */
/* Chart / Table toggle pill                                            */
/* ------------------------------------------------------------------ */

function ViewToggle({ value, onChange }: { value: View; onChange: (v: View) => void }) {
  return (
    <div className="inline-flex bg-chip rounded-full p-0.5 text-[12px]">
      <button
        type="button"
        onClick={() => onChange("chart")}
        className={
          "px-2.5 h-6 rounded-full font-medium transition-colors " +
          (value === "chart" ? "bg-white text-neutral-900 shadow-card" : "text-neutral-500 hover:text-neutral-700")
        }
      >
        Chart
      </button>
      <button
        type="button"
        onClick={() => onChange("table")}
        className={
          "px-2.5 h-6 rounded-full font-medium transition-colors " +
          (value === "table" ? "bg-white text-neutral-900 shadow-card" : "text-neutral-500 hover:text-neutral-700")
        }
      >
        Table
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* TruncationBanner — sits between header and chart when the DB result  */
/* was capped (10k rows). Points the user at the SQL so they can run    */
/* the full query themselves.                                           */
/* ------------------------------------------------------------------ */

function TruncationBanner({
  rowCount,
  canViewSql,
  onViewSql,
}: {
  rowCount: number;
  canViewSql: boolean;
  onViewSql: () => void;
}) {
  const formatted = rowCount.toLocaleString();
  return (
    <div className="mx-5 mb-2 mt-1 flex items-start gap-2 rounded-[10px] border border-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
      <InfoIcon />
      <div className="leading-relaxed">
        Showing the first {formatted} rows. The full result is larger —
        {canViewSql ? (
          <>
            {" "}
            <button
              type="button"
              onClick={onViewSql}
              className="underline font-medium hover:text-amber-950"
            >
              view the SQL
            </button>{" "}
            and run it in your database for the complete dataset.
          </>
        ) : (
          " run the same query in your database to fetch the complete dataset."
        )}
      </div>
    </div>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4 shrink-0 mt-[1px]">
      <circle cx="10" cy="10" r="7.5" />
      <path strokeLinecap="round" d="M10 9v5M10 6.5v.01" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* CustomChartUnsupportedBanner — shown when kind:'custom' but the      */
/* model didn't inline data into the ECharts option (most common cause: */
/* a heatmap or geo viz tried to use rows/columns auto-wiring that      */
/* `kind: "custom"` doesn't have). The chart toggle hides; the table    */
/* view becomes the only renderer for this card.                        */
/* ------------------------------------------------------------------ */

function CustomChartUnsupportedBanner() {
  return (
    <div className="mx-5 mb-2 mt-1 flex items-start gap-2 rounded-[10px] border border-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
      <InfoIcon />
      <div className="leading-relaxed">
        This chart type isn't supported yet — showing the data as a table instead. Try asking for a heatmap, geo_scatter, bar, line, or donut chart.
      </div>
    </div>
  );
}

/**
 * Does the ECharts option carry at least one renderable series?
 *
 * Used by ChartCard to detect the "kind: custom but series.data is
 * empty" case where ECharts would render a blank container. We don't
 * try to deeply validate the option (ECharts has dozens of series
 * types); just check that at least one series declares a non-empty
 * `data` array. If a custom spec uses some other ECharts mechanism
 * (dataset bindings, async load), this check will treat it as empty
 * and fall back to the table — that's a conservative-but-safe
 * mistake; the user still sees their data.
 */
function hasAnySeriesData(option: Record<string, unknown> | null): boolean {
  if (!option) return false;
  const series = option["series"];
  if (!Array.isArray(series)) return false;
  for (const s of series) {
    if (s && typeof s === "object") {
      const data = (s as { data?: unknown }).data;
      if (Array.isArray(data) && data.length > 0) return true;
    }
  }
  return false;
}

function SqlIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4">
      <ellipse cx="12" cy="5" rx="7" ry="2.5" />
      <path d="M5 5v6c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V5" />
      <path strokeLinecap="round" d="M8 17h2M12 17h6M8 20h8" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4">
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2h2" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4">
      <path strokeLinecap="round" d="M3 3l10 10M13 3l-10 10" />
    </svg>
  );
}

function DatabaseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4">
      <ellipse cx="12" cy="6" rx="7" ry="2.5" />
      <path d="M5 6v6c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V6M5 12v6c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-6" />
    </svg>
  );
}

function DashboardAddIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M17.5 14v7M14 17.5h7" strokeLinecap="round" />
    </svg>
  );
}

function ReportPlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 4h7l4 4v8a1 1 0 01-1 1H7a1 1 0 01-1-1V5a1 1 0 011-1z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 4v4h4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 11v6m-3-3h6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12l4 4 10-10" />
    </svg>
  );
}
